import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { FlowBuilderContext } from './FlowBuilderContextProvider';
import {
  FormControl, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import {
  HButton, HDialog, HDialogActions, HDialogContent, HSpinner,
} from 'BaseComponents';
import { useCloningFlow, useFetchFlowVersionHistory } from 'api/flows';
import { useHistory, useParams } from 'react-router-dom';


const getInitialCloneDetailsState = ({ flow }) => ({
  name: `${flow?.name ?? ''} - Copy`,
  description: flow?.description ?? '',
  targetVersion: flow?.version ?? '',
});
const INITIAL_FORM_VALID = false;

const CloneFlowDialog = () => {
  const history = useHistory();
  const { crId, crFlowId } = useParams();
  const {
    cloneFlowDialog,
    handleCloneFlowDialogClose,
    flowId,
    flow,
    cleanRoomFlow,
    goBackUrl,
  } = useContext(FlowBuilderContext);
  const [cloneDetails, setCloneDetails] = useState(() => getInitialCloneDetailsState({ flow }));
  const [isFormValid, setIsFormValid] = useState(INITIAL_FORM_VALID);
  const cloningFlow = useCloningFlow();
  const {
    data: flowVersions, isSuccess: isFlowVersionsSuccess,
  } = useFetchFlowVersionHistory({
    flowId,
    offset: 0,
    limit: 100,
    enabled: cloneFlowDialog.open,
  });
  const versions = useMemo(() => (
    isFlowVersionsSuccess ? flowVersions?.data?.versions || [] : []
  ), [isFlowVersionsSuccess, flowVersions]);

  const resetLocalStates = useCallback(() => {
    setCloneDetails(() => getInitialCloneDetailsState({ flow }));
    setIsFormValid(INITIAL_FORM_VALID);
  }, [flow]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCloneDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    cloningFlow.mutate({
      cleanRoomId: crId,
      sourceCrFlowId: crFlowId,
      newFlowName: cloneDetails.name,
      newFlowDescription: cloneDetails.description,
      targetVersion: cloneDetails.targetVersion,
    });
  };

  useEffect(() => {
    setIsFormValid(cloneDetails.name.trim().length > 0 && cloneDetails.targetVersion >= 0);
  }, [cloneDetails]);

  useEffect(() => {
    if (cloningFlow.isSuccess) {
      // Reset all local states
      resetLocalStates();
      // Close the modal
      handleCloneFlowDialogClose();
      // Send the user to the Clean Room Flows page
      history.push(goBackUrl);
    }
  }, [cloningFlow, resetLocalStates, handleCloneFlowDialogClose, goBackUrl, history]);

  return (
    <HDialog
      open={cloneFlowDialog.open}
      onClose={handleCloneFlowDialogClose}
      title={<Typography variant='h3'>Clone Flow</Typography>}
      maxWidth='md'
      fullWidth
    >
      <HDialogContent>
        <TextField
          fullWidth
          margin='normal'
          label='Flow Name'
          name='name'
          value={cloneDetails.name}
          onChange={handleInputChange}
          required
          error={cloneDetails.name.trim().length === 0}
          helperText={cloneDetails.name.trim().length === 0 ? 'Name is required' : ''}
          variant='outlined'
        />
        <TextField
          fullWidth
          margin='normal'
          label='Flow Description'
          name='description'
          value={cloneDetails.description}
          onChange={handleInputChange}
          multiline
          rows={3}
          variant='outlined'
        />
        <FormControl fullWidth margin='normal'>
          <InputLabel id='version-select-label'>Target Version</InputLabel>
          <Select
            labelId='version-select-label'
            name='targetVersion'
            value={cloneDetails.targetVersion}
            onChange={handleInputChange}
            label='Target Version'
            required
          >
            {versions.map((version) => (
              <MenuItem
                key={version.version}
                value={version.version}
                disabled={version.version < cloneDetails.targetVersion}
              >
                {`v${version.version} ${version.version === cleanRoomFlow?.flowVersion ? '(Current)' : ''}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </HDialogContent>
      <HDialogActions>
        <HButton
          variant='outlined'
          onClick={handleCloneFlowDialogClose}
          disabled={cloningFlow.isLoading}
        >
          Cancel
        </HButton>
        <HButton
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={!isFormValid || cloningFlow.isLoading}
          endIcon={cloningFlow.isLoading ? <HSpinner isButton /> : null}
        >
          Clone Flow
        </HButton>
      </HDialogActions>
    </HDialog>
  );
};

export default CloneFlowDialog;
